import React, { useState, useRef, ChangeEvent } from "react";
import { Button, Form } from "react-bootstrap";
import useFetch from "../../../hooks/useFetch";
import { getAPIHeaders, getAPIMultiPartHeaders } from "../../../common/Utils";
import { API } from "../../../common/Constants";
import SheetSelector from "./SheetSelector";
import BulkUploadResults from "./BulkUploadResults";
import ImageWithBasePath from "../../../core/common/imageWithBasePath";
import PreLoader from "../../../core/common/preLoader";
import "./bulkUpload.css";
import CustomModal from "../../../core/common/customModal";

// Sheet name enumeration (matching Java WorkbookSheetName)
export const SheetTypes = {
  ALL: "All",
  CURRICULUM: "Curriculum",
  COURSE: "Course",
  SUBJECT: "Subject",
  SYLLABUS: "Syllabus",
  BATCH: "Batch",
  STUDENT: "Student",
  DEPARTMENT: "Department",
  DESIGNATION: "Designation",
  EMPLOYEE: "Employee",
};

// UploadType enum (matching Java UploadType)
export const UploadTypes = {
  ALL: "ALL",
  ACADEMICS: "ACADEMICS",
  STUDENT_BATCH: "STUDENT_BATCH",
  EMPLOYEE: "EMPLOYEE",
};

interface UploadTypeMapping {
  [key: string]: {
    label: string;
    sheets: string[];
  };
}

const UPLOAD_TYPE_MAPPINGS: UploadTypeMapping = {
  [UploadTypes.ALL]: {
    label: "All",
    sheets: [SheetTypes.ALL],
  },
  [UploadTypes.ACADEMICS]: {
    label: "Academics",
    sheets: [
      SheetTypes.ALL,
      SheetTypes.CURRICULUM,
      SheetTypes.COURSE,
      SheetTypes.SUBJECT,
      SheetTypes.SYLLABUS,
    ],
  },
  [UploadTypes.STUDENT_BATCH]: {
    label: "Student Batch",
    sheets: [SheetTypes.ALL, SheetTypes.BATCH, SheetTypes.STUDENT],
  },
  [UploadTypes.EMPLOYEE]: {
    label: "Employee",
    sheets: [
      SheetTypes.ALL,
      SheetTypes.DEPARTMENT,
      SheetTypes.DESIGNATION,
      SheetTypes.EMPLOYEE,
    ],
  },
};

interface BulkUploadModalProps {
  show: boolean;
  onHide: () => void;
}

const BulkUploadModal: React.FC<BulkUploadModalProps> = ({ show, onHide }) => {
  const [file, setFile] = useState<File | null>(null);
  const [uploadType, setUploadType] = useState<string>();
  const [selectedSheets, setSelectedSheets] = useState<string[]>([
    SheetTypes.ALL,
  ]);
  const [validationResults, setValidationResults] = useState<any>(null);
  const [uploadResults, setUploadResults] = useState<any>(null);
  const [isValidating, setIsValidating] = useState<boolean>(false);
  const [isUploading, setIsUploading] = useState<boolean>(false);
  const [validationCompleted, setValidationCompleted] =
    useState<boolean>(false);
  const [isLoadingTemplate, setIsLoadingTemplate] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  const { callFetch } = useFetch();

  // Check if validation was successful (no failures in any sheet)
  const isValidationSuccessful = () => {
    if (!validationResults) return false;

    const resultsArray = Array.isArray(validationResults)
      ? validationResults
      : [validationResults];

    return resultsArray.every((result) => result.failureCount === 0);
  };

  // Reset the state when modal is shown/hidden
  const resetModal = () => {
    setFile(null);
    //setUploadType(UploadTypes.ACADEMICS);
    setSelectedSheets([SheetTypes.ALL]);
    setValidationResults(null);
    setUploadResults(null);
    setValidationCompleted(false);
    setError(null);
  };

  // Handle file selection
  const handleFileChange = (e: ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      const selectedFile = e.target.files[0];
      setFile(selectedFile);
      setError(null);
      setValidationResults(null);
      setUploadResults(null);
      setValidationCompleted(false);
    }
  };

  // Handle validation of the uploaded file
  const handleValidate = async () => {
    if (!file) {
      setError("Please select a file to upload");
      return;
    }

    setIsValidating(true);
    setError(null);

    const formData = new FormData();
    formData.append("file", file);

    // Send each sheet name as a separate parameter with the same name
    selectedSheets.forEach((sheet) => {
      formData.append("sheetNames", sheet);
    });

    try {
      const response = await callFetch(
        `${API.VALIDATE_RESOURCES}`,
        "POST",
        getAPIMultiPartHeaders(),
        null,
        formData
      );

      setValidationResults(response);
      setValidationCompleted(true);
      setIsValidating(false);
    } catch (err: any) {
      console.error("Validation error:", err);
      setError(err?.message || "An error occurred during validation");
      setIsValidating(false);
    }
  };

  // Handle the actual upload after validation
  const handleUpload = async () => {
    if (!file || !validationCompleted) {
      setError("Please validate the file before uploading");
      return;
    }

    setIsUploading(true);
    setError(null);

    const formData = new FormData();
    formData.append("file", file);

    // Send each sheet name as a separate parameter with the same name
    selectedSheets.forEach((sheet) => {
      formData.append("sheetNames", sheet);
    });

    try {
      const response = await callFetch(
        `${API.IMPORT_RESOURCES}`,
        "POST",
        getAPIMultiPartHeaders(),
        null,
        formData
      );

      setUploadResults(response);
      setIsUploading(false);
    } catch (err: any) {
      console.error("Upload error:", err);
      setError(err?.message || "An error occurred during upload");
      setIsUploading(false);
    }
  };

  // Handle change of upload type (curriculum, class batch, employee)
  const handleUploadTypeChange = (e: ChangeEvent<HTMLSelectElement>) => {
    const newUploadType = e.target.value;
    setUploadType(newUploadType);
    setSelectedSheets([SheetTypes.ALL]); // Reset to ['All'] when upload type changes
  };

  // Determine if we should show results
  const showResults = validationResults || uploadResults;

  // Handle template download
  const handleDownloadTemplate = async () => {
    // Set loading state
    setIsLoadingTemplate(true);

    try {
      // Call API to get the template URL
      const response = await callFetch(
        `${API.GET_ONBOARDING_TEMPLATE_URL}`,
        "GET",
        getAPIHeaders(),
        null,
        null
      );

      // Check if we have a valid URL in the response
      if (response) {
        if (typeof response === "string" && response.startsWith("http")) {
          // Open the template URL in a new tab
          window.open(response, "_blank");
        }
        // Invalid response format
        else {
          setError("Unable to download template. Please try again later.");
          console.error("Invalid template URL response:", response);
        }
      } else {
        // Show an error if the response doesn't contain a URL
        setError("Unable to download template. Please try again later.");
        console.error("Invalid template URL response:", response);
      }
    } catch (err: any) {
      // Handle any errors during the API call
      console.error("Unable to download template:", err);
      setError(
        err?.message || "An error occurred while retrieving the template URL."
      );
    } finally {
      // Always reset the loading state when done
      setIsLoadingTemplate(false);
    }
  };

  return (
    <CustomModal 
      size="lg"
      show={show} 
      title={"Bulk Upload"} 
      onClose={() => {
          resetModal();
          onHide();
        }}
        headerContent={
          <Button
            variant="outline-primary"
            size="sm"
            className="ms-3"
            onClick={handleDownloadTemplate}
            disabled={isLoadingTemplate}
          >
            {isLoadingTemplate ? (
              <>
                <i className="fa fa-spinner fa-spin me-1"></i> Downloading...
              </>
            ) : (
              <>
                <i className="fa fa-download me-1"></i> Download Template
              </>
            )}
          </Button>
        }

      bodyContent={<>
        {(isValidating || isUploading) && <PreLoader showLoader={true} />}

        {!showResults ? (
          <>
            <Form>
              {/* <Form.Group className="mb-3">
                <Form.Label>Upload Type</Form.Label>
                <Form.Select
                  value={uploadType}
                  onChange={handleUploadTypeChange}
                  disabled={isValidating || isUploading}
                >
                  {Object.entries(UPLOAD_TYPE_MAPPINGS).map(
                    ([type, config]) => (
                      <option key={type} value={type}>
                        {config.label}
                      </option>
                    )
                  )}
                </Form.Select>
              </Form.Group> */}

              <Form.Group className="mb-3">
                <Form.Label>Sheet Selection</Form.Label>
                <SheetSelector
                  availableSheets={Object.values(SheetTypes)}
                  selectedSheets={selectedSheets}
                  onChange={setSelectedSheets}
                  disabled={isValidating || isUploading}
                />
                <Form.Text className="text-muted mt-2">
                  Click on cards to select multiple sheets. Selecting "All" will
                  process all sheets. Other selections will be cleared if "All"
                  is selected.
                </Form.Text>
              </Form.Group>

              <Form.Group className="mb-4">
                <Form.Label>Upload Excel File</Form.Label>
                <div className="file-upload-container border rounded p-3">
                  <div className="d-flex flex-column align-items-center">
                    {file ? (
                      <div className="d-flex align-items-center w-100">
                        <ImageWithBasePath
                          src="assets/img/icons/xls.svg"
                          alt="Excel"
                          className="me-3 excel-icon"
                        />
                        <div className="flex-grow-1">
                          <p className="mb-0 fw-medium text-primary">
                            {file.name}
                          </p>
                          <small className="text-muted">
                            {(file.size / 1024).toFixed(2)} KB
                          </small>
                        </div>
                        <Button
                          variant="outline-secondary"
                          size="sm"
                          onClick={() => setFile(null)}
                          disabled={isValidating || isUploading}
                        >
                          Change
                        </Button>
                      </div>
                    ) : (
                      <>
                        <div className="text-center mb-3">
                          <ImageWithBasePath
                            src="assets/img/icons/xls.svg"
                            alt="Upload"
                            className="upload-icon"
                          />
                          <h6 className="mt-2 mb-1">Select Excel File</h6>
                          <p className="text-muted small mb-3">
                            Only .xlsx and .xls files are supported
                          </p>
                        </div>

                        <div className="w-100 d-flex justify-content-center">
                          <label className="btn btn-outline-primary">
                            Browse Files
                            <Form.Control
                              type="file"
                              accept=".xlsx,.xls"
                              onChange={handleFileChange}
                              disabled={isValidating || isUploading}
                              style={{ display: "none" }}
                            />
                          </label>
                        </div>
                      </>
                    )}
                  </div>

                  {file && (
                    <div className="mt-3 border-top pt-3">
                      <div className="d-flex align-items-center">
                        <div className="text-success me-2">
                          <i className="fa fa-check-circle"></i>
                        </div>
                        <div className="text-muted small">
                          File selected and ready for validation
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </Form.Group>
            </Form>

            {error && (
              <div className="alert alert-danger" role="alert">
                {error}
              </div>
            )}
          </>
        ) : (
          <BulkUploadResults
            results={uploadResults || validationResults}
            isValidation={!uploadResults}
            onSubmit={handleUpload}
            onReset={resetModal}
            inProgress={isUploading || isValidating}
          />
        )}
      </>}

      footerContent={   <>     
        {!showResults ? (
          <>
            <Button
              variant="secondary"
              onClick={onHide}
              style={{ marginRight: "10px" }}
            >
              Cancel
            </Button>
            <Button
              variant="warning"
              onClick={handleValidate}
              disabled={!file || isValidating || isUploading}
              style={{ marginRight: "10px" }}
            >
              {isValidating ? "Validating..." : "Validate"}
            </Button>
            <Button
              variant="primary"
              onClick={handleUpload}
              disabled={
                !validationCompleted ||
                !isValidationSuccessful() ||
                isUploading ||
                isValidating
              }
            >
              {isUploading ? "Uploading..." : "Submit"}
            </Button>
          </>
        ) : (
          <>
            {validationResults &&
              !uploadResults &&
              isValidationSuccessful() && (
                <Button
                  variant="primary"
                  onClick={handleUpload}
                  disabled={isUploading}
                  style={{ marginRight: "10px" }}
                >
                  {isUploading ? "Uploading..." : "Submit"}
                </Button>
              )}
            <Button
              variant="secondary"
              onClick={resetModal}
              style={{ marginRight: "10px" }}
            >
              {uploadResults ? "Upload Another" : "Select Another"}
            </Button>
            <Button variant="primary" onClick={onHide}>
              Close
            </Button>
          </>
        )}</>}
      />
      
      
  );
};

export default BulkUploadModal;
